import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart3, Users, ShoppingBag, DollarSign, Eye, MousePointerClick,
  Globe, Mail, ExternalLink, ArrowUpRight, LogOut, RefreshCcw, Lock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, accent = '#FAD4D4' }) {
  return (
    <div className="border-2 border-[#0A0A0A] card-brutalist p-4 sm:p-5 bg-white">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#525252]">{label}</div>
          <div className="mt-1 text-2xl sm:text-3xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
          {sub && <div className="mt-0.5 text-xs text-[#525252]">{sub}</div>}
        </div>
        <div className="w-9 h-9 border-2 border-[#0A0A0A] flex items-center justify-center" style={{ backgroundColor: accent }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children, className = '' }) {
  return (
    <div className={`border-2 border-[#0A0A0A] card-brutalist bg-white ${className}`}>
      <div className="border-b-2 border-[#0A0A0A] px-4 py-3">
        <h3 className="text-sm font-black uppercase tracking-wider">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Bar({ value, max, label, right }) {
  const pct = max > 0 ? Math.max(4, (value / max) * 100) : 0;
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex justify-between text-xs mb-1">
        <span className="truncate mr-2">{label}</span>
        <span className="font-semibold whitespace-nowrap">{right ?? value.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-[#F5F5F5] border border-[#0A0A0A]">
        <div className="h-full bg-[#0A0A0A]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function money(n) {
  if (typeof n !== 'number') return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Dedicated in-place login form so hitting /admin never bounces you around.
function AdminLoginGate({ signedInAsNonAdmin }) {
  const [email, setEmail] = useState('admin@doewiz.com');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.role !== 'admin') {
        setErr("That account isn't an admin. Please sign in with the admin credentials.");
        setLoading(false);
        return;
      }
      // Success — force full reload so AuthContext picks up cookie and dashboard mounts
      window.location.reload();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Invalid email or password.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-12" data-testid="admin-login-gate">
      <div className="w-full max-w-md">
        <div className="border-2 border-[#0A0A0A] card-brutalist bg-white">
          <div className="border-b-2 border-[#0A0A0A] px-5 py-4 bg-[#0A0A0A] text-white flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <span className="text-sm font-black uppercase tracking-widest">Admin Sign-in</span>
          </div>
          <form onSubmit={submit} className="p-5 sm:p-6 space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#525252]">Doe Wiz · Admin Dashboard</div>
              <h1 className="text-2xl sm:text-3xl font-black mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Sign in to continue</h1>
              <p className="mt-2 text-xs text-[#525252]">Only Doe Wiz admins can view the dashboard.</p>
            </div>

            {signedInAsNonAdmin && (
              <div className="border-2 border-[#0A0A0A] bg-[#FDF0D5] px-3 py-2 text-xs">
                You're signed in but not as an admin. Sign in with the admin account below.
              </div>
            )}

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#525252]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="mt-1 w-full border-2 border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FAD4D4]"
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#525252]">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="mt-1 w-full border-2 border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FAD4D4]"
                data-testid="admin-login-password"
              />
            </div>

            {err && (
              <div className="border-2 border-red-500 bg-red-50 text-red-700 px-3 py-2 text-xs font-semibold" data-testid="admin-login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white py-3 text-sm font-black uppercase tracking-widest hover:bg-[#FAD4D4] hover:text-[#0A0A0A] transition-colors disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="text-[11px] text-[#525252] leading-relaxed pt-2 border-t border-[#0A0A0A]/10">
              <div className="font-semibold uppercase tracking-widest text-[10px] mb-1">Default admin</div>
              email: <span className="font-mono">admin@doewiz.com</span><br />
              password: <span className="font-mono">admin123</span>
              <br /><span className="text-[10px] italic">Change this after your first sign-in.</span>
            </div>

            <Link to="/" className="block text-center text-xs text-[#525252] hover:text-[#0A0A0A] pt-1">← Back to shop</Link>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState(30);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      const [d, o, w, e] = await Promise.all([
        axios.get(`${API}/admin/dashboard?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/admin/orders?limit=25`, { withCredentials: true }),
        axios.get(`${API}/admin/waitlist`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/events?limit=25`, { withCredentials: true }),
      ]);
      setData(d.data);
      setOrders(o.data);
      setWaitlist(w.data);
      setEvents(e.data);
    } catch (err) {
      // handled by role check below
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') return; // Show inline login below
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, days]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }

  // Not signed in OR signed in as non-admin → show a dedicated admin login form right here
  if (!user || user.role !== 'admin') {
    return <AdminLoginGate signedInAsNonAdmin={!!user && user.role !== 'admin'} />;
  }

  const totals = data?.totals || {};
  const maxRef = Math.max(1, ...(data?.top_referrers?.map((r) => r.count) || [0]));
  const maxPage = Math.max(1, ...(data?.top_pages?.map((r) => r.count) || [0]));
  const maxCountry = Math.max(1, ...(data?.top_countries?.map((r) => r.count) || [0]));
  const maxDay = Math.max(1, ...(data?.traffic_by_day?.map((r) => r.pageviews) || [0]));

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-dashboard-page">
      {/* Header strip */}
      <div className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#525252] font-semibold">Doe Wiz · Admin</div>
            <h1 className="text-2xl sm:text-3xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border-2 border-[#0A0A0A] px-3 py-2 text-xs font-bold uppercase bg-white"
              data-testid="admin-range-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
            <button
              onClick={fetchAll}
              disabled={refreshing}
              className="border-2 border-[#0A0A0A] px-3 py-2 text-xs font-bold uppercase bg-white hover:bg-[#FAD4D4] transition-colors disabled:opacity-50 flex items-center gap-1.5"
              data-testid="admin-refresh"
            >
              <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className="border-2 border-[#0A0A0A] px-3 py-2 text-xs font-bold uppercase bg-[#0A0A0A] text-white hover:bg-[#FAD4D4] hover:text-[#0A0A0A] transition-colors flex items-center gap-1.5"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard icon={Eye} label="Pageviews" value={(totals.pageviews || 0).toLocaleString()} sub={`in the last ${days} days`} accent="#FAD4D4" />
          <StatCard icon={Users} label="Unique visitors" value={(totals.unique_sessions || 0).toLocaleString()} sub="anon sessions" accent="#D4E2FA" />
          <StatCard icon={MousePointerClick} label="Product clicks" value={(totals.product_clicks || 0).toLocaleString()} sub="cards clicked" accent="#D5FDD8" />
          <StatCard icon={ShoppingBag} label="Orders" value={(totals.orders_in_range || 0).toLocaleString()} sub={`${totals.orders || 0} all-time`} accent="#FDF0D5" />
          <StatCard icon={DollarSign} label="Revenue" value={money(totals.revenue || 0)} sub="all-time paid orders" accent="#F5E6D3" />
          <StatCard icon={Users} label="Registered users" value={(totals.users || 0).toLocaleString()} sub="all-time signups" accent="#FAD4D4" />
          <StatCard icon={Mail} label="Waitlist (Babies)" value={(totals.waitlist_babies || 0).toLocaleString()} sub="pre-launch emails" accent="#D4E2FA" />
          <StatCard icon={BarChart3} label="Total events" value={(totals.events || 0).toLocaleString()} sub="pv + clicks combined" accent="#D5FDD8" />
        </div>

        {/* Traffic by day sparkline */}
        <Panel title={`Traffic — last ${days} days (pageviews)`}>
          {data?.traffic_by_day?.length ? (
            <div className="flex items-end gap-1 h-40" data-testid="traffic-chart">
              {data.traffic_by_day.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center min-w-0 group">
                  <div className="w-full bg-[#0A0A0A]" style={{ height: `${(d.pageviews / maxDay) * 100}%`, minHeight: '2px' }} />
                  <div className="text-[9px] text-[#525252] mt-1 truncate group-hover:font-bold" title={`${d.date}: ${d.pageviews}`}>
                    {d.date.slice(5)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#525252]">No traffic yet — pageviews will appear here once shoppers start visiting.</p>
          )}
        </Panel>

        {/* 2-column: referrers + top pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Where clicks come from (referrer)">
            {data?.top_referrers?.length ? (
              data.top_referrers.map((r) => <Bar key={r.source} label={r.source} value={r.count} max={maxRef} />)
            ) : <p className="text-sm text-[#525252]">Waiting for traffic…</p>}
          </Panel>
          <Panel title="Top pages">
            {data?.top_pages?.length ? (
              data.top_pages.map((r) => <Bar key={r.path} label={r.path} value={r.count} max={maxPage} />)
            ) : <p className="text-sm text-[#525252]">Waiting for traffic…</p>}
          </Panel>
        </div>

        {/* UTM campaigns + Countries */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Ad / UTM campaigns">
            {data?.top_utm?.length ? (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b-2 border-[#0A0A0A]">
                    <th className="text-left py-2 font-bold uppercase">Source</th>
                    <th className="text-left py-2 font-bold uppercase">Medium</th>
                    <th className="text-left py-2 font-bold uppercase">Campaign</th>
                    <th className="text-right py-2 font-bold uppercase">Hits</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_utm.map((u, i) => (
                    <tr key={i} className="border-b border-[#0A0A0A]/10">
                      <td className="py-2">{u.source || '—'}</td>
                      <td className="py-2 text-[#525252]">{u.medium || '—'}</td>
                      <td className="py-2 text-[#525252]">{u.campaign || '—'}</td>
                      <td className="py-2 text-right font-bold">{u.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-[#525252]">Tag your ad links with <code className="bg-[#F5F5F5] px-1">?utm_source=…&utm_medium=…</code> to see attribution here.</p>
            )}
          </Panel>
          <Panel title="Countries">
            {data?.top_countries?.length ? (
              data.top_countries.map((r) => <Bar key={r.country} label={r.country || 'Unknown'} value={r.count} max={maxCountry} />)
            ) : <p className="text-sm text-[#525252]">No country data yet.</p>}
          </Panel>
        </div>

        {/* Top clicked products */}
        <Panel title="Most-clicked products">
          {data?.top_products?.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {data.top_products.map((p) => (
                <Link key={p.product_id} to={`/product/${p.product_id}`} className="border-2 border-[#0A0A0A] hover:bg-[#FAD4D4] transition-colors block">
                  <div className="aspect-square overflow-hidden border-b-2 border-[#0A0A0A] bg-[#F5F5F5]">
                    {p.images?.[0] && <img src={p.images[0].startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${p.images[0]}` : p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] uppercase font-bold text-[#525252] tracking-wider">{p.count} clicks</div>
                    <div className="text-xs font-semibold line-clamp-2 mt-0.5">{p.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : <p className="text-sm text-[#525252]">Once shoppers click product cards this list will populate.</p>}
        </Panel>

        {/* Recent orders */}
        <Panel title="Recent orders">
          {orders.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-[#0A0A0A]">
                    <th className="text-left py-2 font-bold uppercase">Date</th>
                    <th className="text-left py-2 font-bold uppercase">Email</th>
                    <th className="text-left py-2 font-bold uppercase">Status</th>
                    <th className="text-right py-2 font-bold uppercase">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id || o.session_id} className="border-b border-[#0A0A0A]/10">
                      <td className="py-2 whitespace-nowrap">{(o.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-2">{o.user_email || o.customer_email || '—'}</td>
                      <td className="py-2 uppercase text-[10px] font-bold">{o.payment_status || 'pending'}</td>
                      <td className="py-2 text-right font-bold">{money(o.amount_total || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-[#525252]">No orders yet.</p>}
        </Panel>

        {/* Waitlist */}
        <Panel title={`Babies waitlist (${waitlist.length})`}>
          {waitlist.length ? (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b-2 border-[#0A0A0A]">
                    <th className="text-left py-2 font-bold uppercase">Email</th>
                    <th className="text-left py-2 font-bold uppercase">Category</th>
                    <th className="text-left py-2 font-bold uppercase">Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.map((w, i) => (
                    <tr key={i} className="border-b border-[#0A0A0A]/10">
                      <td className="py-2">{w.email}</td>
                      <td className="py-2 uppercase text-[10px] font-bold">{w.category}</td>
                      <td className="py-2 whitespace-nowrap text-[#525252]">{(w.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-[#525252]">No waitlist signups yet.</p>}
        </Panel>

        {/* Raw events (developer view) */}
        <Panel title={`Latest ${events.length} events (raw)`}>
          <div className="overflow-x-auto max-h-72 overflow-y-auto">
            <table className="w-full text-[11px] min-w-[600px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b-2 border-[#0A0A0A]">
                  <th className="text-left py-2 font-bold uppercase">Time</th>
                  <th className="text-left py-2 font-bold uppercase">Event</th>
                  <th className="text-left py-2 font-bold uppercase">Path</th>
                  <th className="text-left py-2 font-bold uppercase">Ref/UTM</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={i} className="border-b border-[#0A0A0A]/10">
                    <td className="py-2 whitespace-nowrap">{(e.created_at || '').slice(11, 19)}</td>
                    <td className="py-2 uppercase font-bold text-[10px]">{e.event_type}</td>
                    <td className="py-2 truncate max-w-[220px]" title={e.path}>{e.path}</td>
                    <td className="py-2 text-[#525252] truncate max-w-[220px]" title={e.referrer || e.utm_source || ''}>
                      {e.utm_source ? `${e.utm_source}${e.utm_medium ? '/' + e.utm_medium : ''}` : (e.referrer || '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <div className="text-[10px] text-[#525252] text-center pt-2 pb-6">
          Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'} · Range {days}d
        </div>
      </div>
    </div>
  );
}
